import { equal, notEqual } from '@/api/helpers';
import { Account, AccountADFormValues } from '@/client/accounts';
import { AccountGoogleWorkspaceFormValues } from '@/client/accounts/types/account-google-workspace.type';
import {
  UserManagementAddToType,
  UserManagementFormIndexValueMapping,
  UserManagementFormMapping,
} from '@/client/accounts/types/account-user-management.type';
import { Actions, Subjects } from '@/client/users';
import {
  availableLanguagesDropdown,
  courseOnlyLanguages,
} from '@/common/constants/languages';
import { useAppSelector } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { AppButton } from '@/ui/buttons';
import { AppChip } from '@/ui/chip';
import { FlexContainer } from '@/ui/styled-ui';
import { branchAdminCheck } from '@/utils/helpers';
import { preventDropdownBrowserTranslate } from '@/utils/helpers/ui.helper';
import { Field, FormikTouched } from 'formik';
import { Chip, ChipRemoveEvent } from 'primereact/chip';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FormikDropdown } from '../form';
import { BranchesSelectInput, GroupsMultiselectInput } from '../form/selectors';

const StyledFlexContainer = styled(FlexContainer)`
  position: relative;
  flex-direction: column;
  border: 0;
  background: var(--white-main);
  border-radius: var(--small-border-radius);
  padding: var(--medium-big-padding);
  margin-top: 20px !important;

  label {
    font-weight: 600;
  }

  .close-icon {
    top: var(--semi-medium-padding);
    right: var(--semi-medium-padding);
  }

  .close-icon.p-button {
    width: 44px;
    height: 44px;
    border-radius: 999px;
    border: 1px solid var(--gray-pale);
    background: var(--white-main);
  }

  .close-icon {
    position: absolute;
    top: var(--semi-medium-padding);
    right: var(--semi-medium-padding);
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;

    &.p-button-secondary:enabled:focus {
      box-shadow: none !important;
    }
  }
`;

const StyledChip = styled(Chip)`
  height: var(--default-input-height);
  border-radius: var(--default-border-radius);
  padding: 10px var(--default-padding);
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  background-color: var(--beige-main);
  color: var(--black-main);
`;

const StyledDropdown = styled(Dropdown)`
  .p-dropdown-label {
    display: flex;
    align-items: center;
    gap: var(--xxsmall-padding);
    white-space: normal;
  }

  .p-chip {
    border-radius: var(--medium-border-radius);
    font-size: var(--xsmall-font-size);
    line-height: var(--xsmall-line-height);
  }
`;

type UserManagementGroupSelectionPropsType = {
  title: string;
  groupLabel: string;
  index: string;
  values: UserManagementFormIndexValueMapping;
  group: UserManagementFormMapping;
  account: Account;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  errors: any;
  touched: FormikTouched<
    AccountADFormValues | AccountGoogleWorkspaceFormValues
  >;
  setFieldTouched: (
    field: string,
    isTouched?: boolean | undefined,
    shouldValidate?: boolean | undefined,
  ) => void;
  disabled?: boolean;
};

export const UserManagementGroupSelection: React.FC<
  UserManagementGroupSelectionPropsType
> = ({
  groupLabel,
  index,
  values,
  group,
  account,
  setFieldValue,
  errors,
  touched,
  setFieldTouched,
  disabled,
}) => {
  const { can } = usePermission();
  const { t } = useTranslation();
  const [showAccount, setShowAccount] = useState(!group.branches.length);
  const [showBranches, setShowBranches] = useState(!!group.branches.length);
  const [showGroups, setShowGroups] = useState(!!group.groups.length);
  const currentUser = useAppSelector(selectCurrentUser);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const isBranchAdmin = branchAdminCheck(currentUser, currentAccount);
  const canManageGroups = can(Actions.READ, Subjects.GROUPS) && !isBranchAdmin;

  const setCurrentMapping = (key: string, value: any) => {
    if (!(touched.mapping && touched.mapping[index])) {
      setFieldTouched(`mapping.${index}`, true);
    }
    setFieldValue('mapping', {
      ...values,
      [index]: { ...group, [key]: value },
    });
  };

  const removeCurrentMapping = () => {
    delete values[index];
    setFieldValue('mapping', values, true);
  };

  const addToDropdownOptions = useMemo(
    () => [
      {
        label: t('account'),
        value: UserManagementAddToType.ACCOUNT,
        disabled: !!showAccount,
      },
      {
        label: t('branch'),
        value: UserManagementAddToType.BRANCH,
        disabled: !!showBranches,
      },
      ...(canManageGroups
        ? [
            {
              label: t('group'),
              value: UserManagementAddToType.GROUP,
              disabled: !!showGroups,
            },
          ]
        : []),
    ],
    [t, showAccount, showBranches, showGroups, canManageGroups],
  );

  const activeTargets = useMemo((): UserManagementAddToType[] => {
    const targets = [
      showBranches
        ? UserManagementAddToType.BRANCH
        : UserManagementAddToType.ACCOUNT,
    ];

    if (showGroups) targets.push(UserManagementAddToType.GROUP);

    return targets;
  }, [showBranches, showGroups]);

  const onAddToChange = (e: DropdownChangeEvent) => {
    const v = e.value as UserManagementAddToType | null;

    if (!v) return;

    if (!(touched.mapping && touched.mapping[index])) {
      setFieldTouched(`mapping.${index}`, true);
    }

    if (v === UserManagementAddToType.ACCOUNT) {
      setShowBranches(false);
      setShowAccount(true);
      setCurrentMapping('branches', []);
      setCurrentMapping('account', account);
    } else if (v === UserManagementAddToType.BRANCH) {
      setShowAccount(false);
      setShowBranches(true);
      setCurrentMapping('account', undefined);
    } else if (v === UserManagementAddToType.GROUP) {
      setShowGroups(true);
      setFieldTouched(`mapping.${index}.groups`, true);
    }
  };

  const hasError =
    !!errors.mapping &&
    errors.mapping[index] &&
    touched.mapping &&
    touched.mapping[index];

  return (
    <>
      <StyledFlexContainer className="mt-2">
        <AppButton
          className="close-icon"
          icon="pi pi-times"
          severity="secondary"
          type="text"
          rounded
          onClick={removeCurrentMapping}
          isDisabled={Object.keys(values).length === 1 || disabled}
        />
        <FlexContainer justify="flex-start" align="flex-end">
          <div className="field flex-1">
            <label htmlFor="groupId">
              {groupLabel}
              <span className="red"> *</span>
            </label>
            <InputText
              defaultValue={group?.groupId}
              onBlur={(event: any) =>
                setCurrentMapping('groupId', event.target.value)
              }
              className="w-full"
              disabled={disabled}
            />
          </div>
          <StyledDropdown
            inputId="add-to-group"
            className="white-background ml-2 mb-3 flex-1"
            value={null}
            placeholder={t('generic.addTo')}
            valueTemplate={() =>
              activeTargets.map((target) => (
                <AppChip
                  key={target}
                  type="white"
                  size="sm"
                  label={t(target)}
                  removable={
                    target === UserManagementAddToType.GROUP &&
                    canManageGroups &&
                    !disabled
                  }
                  removeIcon="pi pi-times"
                  onRemove={(e: ChipRemoveEvent) => {
                    e.originalEvent.stopPropagation();
                    setShowGroups(false);
                    setCurrentMapping('groups', []);

                    return true;
                  }}
                />
              ))
            }
            options={addToDropdownOptions}
            optionLabel="label"
            optionValue="value"
            onChange={onAddToChange}
            disabled={disabled}
            {...preventDropdownBrowserTranslate}
          />
        </FlexContainer>

        <div className="field w-full mb-4">
          <Field
            value={group.language}
            id="language"
            label={t('generic.defaultLanguage')}
            name={`mapping.${index}.language`}
            filter
            onChange={(e: DropdownChangeEvent) =>
              setCurrentMapping('language', e.value)
            }
            onBlur={() => setFieldTouched(`mapping.${index}`, true)}
            className="w-full"
            component={FormikDropdown}
            placeholder={t('generic.select')}
            options={availableLanguagesDropdown.filter(
              ({ value }) => !courseOnlyLanguages.includes(value),
            )}
            required
            disabled={disabled}
            {...preventDropdownBrowserTranslate}
          />
        </div>

        {showAccount && (
          <div className="field w-full">
            <label htmlFor="account">{`${account?.platformSettings?.name} ${t(
              'account',
            )}`}</label>
            <StyledChip
              className={disabled ? 'w-full p-disabled' : 'w-full'}
              label={account?.name}
            />
          </div>
        )}

        {showBranches && (
          <div className="field w-full">
            <label htmlFor="branches">{`${account?.platformSettings?.name} ${t(
              'branch',
            )}`}</label>
            <BranchesSelectInput
              className="w-full"
              onChange={(branch) => setCurrentMapping('branches', [branch])}
              additionalFilters={[
                equal('account', account?.id),
                equal('active', true),
              ]}
              selectedValue={group.branches[0]}
              isDisabled={disabled}
            />
          </div>
        )}

        {showGroups && canManageGroups && (
          <FlexContainer justify="flex-start" align="flex-end">
            <div className="field w-full">
              <label htmlFor="groups">{`${account?.platformSettings?.name} ${t(
                'group',
              )}`}</label>
              <GroupsMultiselectInput
                className="w-full"
                onChange={(groups) => setCurrentMapping('groups', groups)}
                additionalFilters={[
                  equal('account', account?.id),
                  equal('active', true),
                  notEqual('name', 'Phishing Vulnerable'),
                ]}
                selectedOptions={group.groups}
                isDisabled={disabled}
                accountId={account?.id}
              />
            </div>
            <AppButton
              icon="pi pi-times"
              severity="secondary"
              className="white-background ml-2 mb-3"
              rounded
              onClick={() => {
                setShowGroups(false);
                setCurrentMapping('groups', []);
              }}
              isDisabled={disabled}
            />
          </FlexContainer>
        )}

        {hasError && (
          <small className="p-error align-self-start">
            {Object.values(errors.mapping[index]).join(', ')}
          </small>
        )}
      </StyledFlexContainer>
    </>
  );
};
